import { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardHeader,
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TablePagination,
  Typography
} from '@material-ui/core';
import Scrollbar from '../Scrollbar';
import SearchNotFound from '../SearchNotFound';
import { UserListHead, UserListToolbar } from '../user';
import { formatCurrency } from '../../utils/formatCurrency';

const TABLE_HEAD = [
  { id: 'partNumber', label: 'Part Number', alignRight: false },
  { id: 'billableQuantity', label: 'Billable Quantity', alignRight: false },
  { id: 'unitPrice', label: 'Unit Price', alignRight: true },
  { id: 'monthlyCharge', label: 'Monthly Charge', alignRight: true }
];

TeamPartNumbersTable.propTypes = {
  partNumbers: PropTypes.array.isRequired,
  onPartNumberClick: PropTypes.func
};

TeamPartNumbersTable.defaultProps = {
  onPartNumberClick: null
};

// Mirrors the mock HTML: one row per part number, click anywhere on the row
// to open the Part Number Deployment page. Clicking the link in the
// Part Number cell does the same without re-triggering the row click.
export default function TeamPartNumbersTable({ partNumbers, onPartNumberClick }) {
  const [page, setPage] = useState(0);
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredRows = useMemo(() => {
    if (!filterName) return partNumbers;
    const searchLower = filterName.toLowerCase();
    return partNumbers.filter(
      (row) =>
        row.id.toLowerCase().includes(searchLower) ||
        row.description.toLowerCase().includes(searchLower) ||
        row.profile.toLowerCase().includes(searchLower)
    );
  }, [partNumbers, filterName]);

  const totalMonthlyCharge = useMemo(
    () => filteredRows.reduce((sum, row) => sum + row.monthlyCharge, 0),
    [filteredRows]
  );

  const isNotFound = filteredRows.length === 0;
  const hasResults = filteredRows.length > 0;

  const handleRowClick = (row) => {
    if (onPartNumberClick) onPartNumberClick(row);
  };

  return (
    <Card>
      <CardHeader
        title="Billable Items"
        subheader="Each row groups billable items by part number. Billable Quantity is the combined active quantity across all deployments."
      />
      <UserListToolbar
        placeholder="Search LRU, profile, or part number..."
        filterName={filterName}
        onFilterName={handleFilterByName}
      />
      <Scrollbar>
        <TableContainer sx={{ minWidth: 900 }}>
          <Table>
            <UserListHead
              order="asc"
              orderBy="partNumber"
              headLabel={TABLE_HEAD}
              rowCount={partNumbers.length}
              onRequestSort={() => {}}
            />
            <TableBody>
              {filteredRows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => (
                  <TableRow
                    hover
                    key={row.id}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleRowClick(row)}
                  >
                    <TableCell align="left">
                      <Box
                        component="button"
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleRowClick(row);
                        }}
                        sx={{
                          border: 0,
                          background: 'transparent',
                          padding: 0,
                          color: 'primary.main',
                          font: 'inherit',
                          fontWeight: 600,
                          textDecoration: 'underline',
                          cursor: 'pointer'
                        }}
                      >
                        {row.id}
                      </Box>
                    </TableCell>
                    <TableCell align="left">
                      <strong>{row.billableQuantity}</strong>
                    </TableCell>
                    <TableCell align="right">{formatCurrency(row.unitPrice)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {formatCurrency(row.monthlyCharge)}
                    </TableCell>
                  </TableRow>
                ))}
              {isNotFound && (
                <TableRow>
                  <TableCell align="center" colSpan={4} sx={{ py: 3 }}>
                    <SearchNotFound searchQuery={filterName} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            {hasResults && (
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={3} align="right">
                    <Typography variant="subtitle2">Total Monthly Charge</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {formatCurrency(totalMonthlyCharge)}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
      </Scrollbar>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={filteredRows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Card>
  );
}
